// Les séances d'une demande : celles de la personne absente sur les jours choisis, leur état (À rechercher / Remplaçant·e trouvé·e / Déjà demandé /
// Pas de remplaçant·e à trouver), ce qu'il reste à faire avant d'envoyer, la case « Aussi pour… », et le corps de la requête de création.
// Logique pure (sans React), testée par seancesDemande.test.ts.
import type { Atelier, Demande, Personne, SeanceProgramme } from '../../../types';
import type { CorpsCreation, LigneCreation } from './typesCreation';
import { jourCourt, type DateIso } from './dates';

export const cleSeance = (atelierId: number, date: DateIso): string => `${atelierId}_${date}`;

/** Personnes qu'on peut prévenir personnellement pour une séance (le serveur refuse au-delà : server/utils/prevenus.ts). */
export const MAX_PREVENUS_PAR_SEANCE = 10;

/** 'rech' À rechercher · 'trouve' Remplaçant·e trouvé·e · 'deja' Déjà demandé · 'off' Pas de remplaçant·e à trouver */
export type EtatSeance = 'rech' | 'trouve' | 'deja' | 'off';

export interface Seance {
  cle: string;
  atelierId: number;
  atelierNom: string;
  date: DateIso;
  debut: string | null;     // « 14:00:00 »
  fin: string | null;
  lieu: string | null;
  poleId: number | null;
  /** Qui la mène ce jour-là (Eveil) : sert à dire « anime X à ce moment » aux autres séances du même créneau. */
  animateurId: number | null;
  coAnimateurId: number | null;
  /** Ajoutée à la main (« + Un autre de vos ateliers ce jour-là ») : pas de séance prévue ce jour-là dans le programme. */
  ajoutee: boolean;
  /** Une demande existe déjà (non supprimée) : jamais recréée. */
  existante: Demande | null;
  /**
   * Cette demande n'est qu'un brouillon repéré dans Tipee, jamais envoyé, et c'est la personne connectée qui demande pour elle-même : la séance attend
   * son geste (« Envoyer ma demande de remplacement », dans l'aperçu). Ce n'est donc PAS « rien à refaire ».
   */
  aEnvoyer: boolean;
  /** Prise en charge par l'équipe gestion (demande réservée) : jamais recréée, rien d'autre n'est dit. */
  priseEnCharge: boolean;
}

export interface ChoixSeance {
  remp: number | null;
  garant: number | null;
  /** « Prévenir aussi personnellement » : personnes qui reçoivent en plus un message personnel (séance « À rechercher » seulement). */
  prevenir: number[];
  /** Qui avait la séance avant que la case « Aussi pour… » la lui prenne : décocher la case le lui rend. */
  avant: { remp: number; garant: number | null } | null;
}
export interface ChoixEtat {
  choix: Record<string, ChoixSeance>;
  /** « Pas de remplaçant·e à trouver » */
  off: Record<string, true>;
}
export const choixVide = (): ChoixSeance => ({ remp: null, garant: null, prevenir: [], avant: null });
export const lireChoix = (e: ChoixEtat, cle: string): ChoixSeance => e.choix[cle] ?? choixVide();

// ── Construction ────────────────────────────────────────────────────────────────────────────────────────
export interface SourcesSeances {
  absentId: number | null;
  seances: SeanceProgramme[];                          // programme de toute l'institution sur les mois chargés
  ateliers: Record<number, Atelier>;
  existantes: Demande[];                               // demandes non supprimées de la personne (terminées comprises : l'index unique les compte)
  reservees: { atelier_id: number; date_atelier: DateIso }[];
  ajoutees: string[];                                  // clés « autre atelier ce jour-là »
  /** La personne absente est la personne connectée (elle seule peut envoyer son brouillon Tipee). */
  pourMoi: boolean;
}

const parHeure = (a: Seance, b: Seance) => (a.debut ?? '99').localeCompare(b.debut ?? '99') || a.atelierNom.localeCompare(b.atelierNom, 'fr');

/**
 * Séances de la personne absente un jour donné : celles dont elle est titulaire de l'atelier OU qu'elle mène ce jour-là, OU qu'elle co-anime
 * sur un atelier en alternance (DECISIONS §36 — rien ne dit qui anime quelle semaine) (Eveil : la détection Tipee
 * prend la personne qui MÈNE la séance), plus les ateliers qu'on a ajoutés à la main. Une séance menée par quelqu'un d'autre que le/la titulaire reste
 * montrée au/à la titulaire (« Déjà confiée » se règle dans la fenêtre) : on ne cache rien qu'on montrait avant.
 */
export function seancesDuJour(src: SourcesSeances, date: DateIso): Seance[] {
  if (src.absentId == null) return [];
  const exist = new Map(src.existantes.map((d) => [cleSeance(d.atelier_id, String(d.date_atelier).slice(0, 10)), d]));
  const reserv = new Set(src.reservees.map((r) => cleSeance(r.atelier_id, String(r.date_atelier).slice(0, 10))));
  const construire = (a: Atelier, p: SeanceProgramme | null): Seance => {
    const cle = cleSeance(a.id, date);
    const existante = exist.get(cle) ?? null;
    return {
      cle, atelierId: a.id, atelierNom: p?.atelier_nom ?? a.nom, date,
      debut: p?.heure_debut ?? a.heure_debut, fin: p?.heure_fin ?? a.heure_fin, lieu: p?.lieu ?? a.lieu,
      poleId: a.pole_id, animateurId: p?.animateur_personne_id ?? null, coAnimateurId: p?.co_animateur_personne_id ?? null,
      ajoutee: p == null, existante, priseEnCharge: reserv.has(cle) && !exist.has(cle),
      aEnvoyer: src.pourMoi && existante?.statut === 'brouillon' && existante.source === 'tipee',
    };
  };
  const vus = new Set<number>();
  const out: Seance[] = [];
  for (const p of src.seances) {
    const a = src.ateliers[p.atelier_id];
    if (p.date !== date || !a || vus.has(a.id)) continue;
    const mene = a.titulaire_personne_id === src.absentId || p.animateur_personne_id === src.absentId;
    // Atelier en alternance (DECISIONS §36) : la 2e personne du créneau peut être celle qui anime ce jour-là.
    const alterne = a.mode_coanimation === 'alternance' && p.co_animateur_personne_id === src.absentId;
    if (!mene && !alterne) continue;
    vus.add(a.id);
    out.push(construire(a, p));
  }
  for (const cle of src.ajoutees) {
    const [id, d] = cle.split('_');
    const a = src.ateliers[Number(id)];
    if (d === date && a && !vus.has(a.id)) { vus.add(a.id); out.push(construire(a, null)); }
  }
  return out.sort(parHeure);
}

/** Séances de tous les jours choisis, dans l'ordre. */
export function seancesDesJours(src: SourcesSeances, jours: DateIso[]): Seance[] {
  return [...jours].sort().flatMap((d) => seancesDuJour(src, d));
}

/** Les autres ateliers de la personne, pour « + Un autre de vos ateliers ce jour-là » : pas déjà au programme ce jour-là, pas déjà ajoutés. */
export function ateliersAjoutables(src: SourcesSeances, date: DateIso): Atelier[] {
  if (src.absentId == null) return [];
  const deja = new Set(seancesDuJour(src, date).map((s) => s.atelierId));
  return Object.values(src.ateliers)
    .filter((a) => a.titulaire_personne_id === src.absentId && a.actif !== false && !deja.has(a.id))
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

// ── État d'une séance, compteurs, ce qu'il reste à faire ────────────────────────────────────────────────
export function statut(s: Seance, e: ChoixEtat): EtatSeance {
  if (s.existante || s.priseEnCharge) return 'deja';
  if (e.off[s.cle]) return 'off';
  return lireChoix(e, s.cle).remp != null ? 'trouve' : 'rech';
}

/** `aEnvoyer` : parmi les « Déjà demandé », les brouillons repérés dans Tipee qui attendent l'envoi de la personne (voir Seance.aEnvoyer). */
export interface Compteurs { rech: number; trouve: number; deja: number; off: number; total: number; aEnvoyer: number }
export function compteurs(seances: Seance[], e: ChoixEtat): Compteurs {
  const c: Compteurs = { rech: 0, trouve: 0, deja: 0, off: 0, total: 0, aEnvoyer: 0 };
  for (const s of seances) { c[statut(s, e)]++; c.total++; if (s.aEnvoyer) c.aEnvoyer++; }
  return c;
}

export interface Manque { cle: 'absent' | 'dates' | 'donnees' | 'garant' | 'motif' | 'tipee'; txt: string; cleSeance?: string }
/** Les données dont dépend la liste des séances (programme, ateliers, demandes déjà faites) : prêtes, en cours de chargement, ou en échec (`txt` = quoi faire). */
export type EtatDonnees = { etat: 'pretes' } | { etat: 'chargement' } | { etat: 'erreur'; txt: string };
export interface EntreesManques {
  gestion: boolean;                 // la personne absente se choisit (sinon : c'est soi)
  absentId: number | null;
  jours: DateIso[];
  seances: Seance[];
  e: ChoixEtat;
  personnes: Record<number, Personne>;
  /** Au moins une « autre activité » sur un jour choisi. */
  autreActivite: boolean;
  motif: 'vacances' | 'autre' | null;
  tipee: boolean;
  donnees: EtatDonnees;
  voix: (vous: string, tiers: string) => string;
}
/** Ce qui manque pour envoyer, dans l'ordre du panneau. Chaque texte dit quoi faire. */
export function manques(x: EntreesManques): Manque[] {
  const m: Manque[] = [];
  if (x.gestion && x.absentId == null) m.push({ cle: 'absent', txt: 'Choisissez la personne absente.' });
  const c = compteurs(x.seances, x.e);
  if (!x.jours.length) m.push({ cle: 'dates', txt: x.voix("Choisissez vos jours d'absence dans le calendrier.", "Choisissez les jours d'absence dans le calendrier.") });
  else if (x.absentId != null && x.donnees.etat !== 'pretes') {
    // Tant que le programme ou les demandes déjà faites ne sont pas là, on ne sait pas quelles séances il y a : on ne conclut rien.
    m.push({ cle: 'donnees', txt: x.donnees.etat === 'erreur' ? x.donnees.txt : x.voix('Chargement de vos séances…', 'Chargement des séances…') });
  } else if (x.absentId != null && c.rech + c.trouve === 0) {
    m.push({
      cle: 'dates',
      txt: x.autreActivite ? 'Aucune séance à envoyer : une autre activité seule ne crée pas de demande.'
        : c.aEnvoyer > 0 ? `${c.aEnvoyer > 1 ? 'Des séances repérées' : 'Une séance repérée'} dans Tipee ${c.aEnvoyer > 1 ? 'attendent' : 'attend'} votre envoi : cliquez-${c.aEnvoyer > 1 ? 'en une' : 'la'}, puis « Envoyer ma demande de remplacement ».`
        : c.deja > 0 && c.deja === c.total ? 'Ces séances sont déjà demandées : rien à envoyer.'
        : 'Aucune séance à envoyer sur ces jours.',
    });
  }
  for (const s of x.seances) {
    const ch = x.e.choix[s.cle];
    const p = ch?.remp != null ? x.personnes[ch.remp] : null;
    if (statut(s, x.e) === 'trouve' && p?.is_stagiaire && ch?.garant == null) {
      m.push({ cle: 'garant', cleSeance: s.cle, txt: `Choisissez le/la garant·e du cadre (${jourCourt(s.date)}).` });
    }
  }
  if (!x.motif) m.push({ cle: 'motif', txt: "Indiquez d'abord le motif de l'absence." });
  if (!x.tipee) m.push({ cle: 'tipee', txt: "Répondez d'abord au rappel Tipee." });
  return m;
}

// ── « Aussi pour… » (même personne sur les autres séances du même atelier) ─────────────────────────────
/** Autres séances du même atelier qu'on peut encore envoyer (ni « Déjà demandé »). */
export const memesAtelier = (seances: Seance[], s: Seance): Seance[] =>
  seances.filter((o) => o.atelierId === s.atelierId && o.cle !== s.cle && !o.existante && !o.priseEnCharge);

/**
 * Séances que la case « Aussi pour… » concerne : les autres séances du même atelier, sauf « Pas de remplaçant·e à trouver ». Une séance déjà
 * confiée à quelqu'un d'autre en fait partie : la case le dit (« au lieu de David Viale »), et la décocher lui rend son choix d'avant.
 */
export function liees(seances: Seance[], e: ChoixEtat, s: Seance): Seance[] {
  if (lireChoix(e, s.cle).remp == null) return [];
  return memesAtelier(seances, s).filter((o) => !e.off[o.cle]);
}
/** La case est cochée quand toutes ces séances ont la même personne. */
export function estLie(seances: Seance[], e: ChoixEtat, s: Seance): boolean {
  const l = liees(seances, e, s);
  const remp = lireChoix(e, s.cle).remp;
  return l.length > 0 && l.every((o) => lireChoix(e, o.cle).remp === remp);
}
/**
 * Pour chaque séance liée, ce qu'il y avait avant (pour dire « au lieu de … ») : le choix actuel s'il est différent, sinon celui qu'elle a
 * quitté en prenant la même personne.
 */
export function auLieuDe(e: ChoixEtat, s: Seance, o: Seance): number | null {
  const remp = lireChoix(e, s.cle).remp;
  const co = lireChoix(e, o.cle);
  if (co.remp != null && co.remp !== remp) return co.remp;
  return co.remp === remp && co.avant ? co.avant.remp : null;
}

const cloner = (e: ChoixEtat): ChoixEtat => ({ choix: { ...e.choix }, off: { ...e.off } });

/** Confie la séance à `remp` (null = de nouveau « À rechercher »). Ne touche jamais aux autres séances. */
export function designer(e: ChoixEtat, cle: string, remp: Personne | null): ChoixEtat {
  const n = cloner(e);
  const c = lireChoix(n, cle);
  n.choix[cle] = { ...c, remp: remp?.id ?? null, garant: remp?.is_stagiaire ? (c.remp === remp.id ? c.garant : null) : null, avant: null };
  delete n.off[cle];
  return n;
}
export function choisirGarant(e: ChoixEtat, cles: string[], garantId: number | null): ChoixEtat {
  const n = cloner(e);
  for (const cle of cles) n.choix[cle] = { ...lireChoix(n, cle), garant: garantId };
  return n;
}
/**
 * Le/la garant·e du cadre d'une séance. Quand la case « Aussi pour… » est cochée, il/elle suit la même personne sur les autres séances du même
 * atelier (sauf celles où un·e autre garant·e a déjà été choisi·e à la main) ; sinon, seule cette séance change.
 */
export function appliquerGarant(seances: Seance[], e: ChoixEtat, s: Seance, garantId: number | null): ChoixEtat {
  const c = lireChoix(e, s.cle);
  const suivent = c.remp == null || !estLie(seances, e, s) ? [] : liees(seances, e, s)
    .filter((o) => { const co = lireChoix(e, o.cle); return co.remp === c.remp && (co.garant == null || co.garant === c.garant); })
    .map((o) => o.cle);
  return choisirGarant(e, [s.cle, ...suivent], garantId);
}

/** Case cochée : même personne (et même garant·e) sur les séances liées, en retenant qui elles remplacent. Décochée : chacune retrouve son choix d'avant. */
export function lier(seances: Seance[], e: ChoixEtat, s: Seance, oui: boolean): ChoixEtat {
  const n = cloner(e);
  const c = lireChoix(n, s.cle);
  for (const o of liees(seances, e, s)) {
    const co = lireChoix(n, o.cle);
    if (oui) {
      if (co.remp !== c.remp) n.choix[o.cle] = { ...co, avant: co.remp != null ? { remp: co.remp, garant: co.garant } : null, remp: c.remp, garant: c.garant };
    } else if (co.remp === c.remp) {
      n.choix[o.cle] = { ...co, remp: co.avant?.remp ?? null, garant: co.avant?.garant ?? null, avant: null };
    }
  }
  return n;
}
/** Puce « Prévenir aussi personnellement » : cochée / décochée. Au plafond (MAX_PREVENUS_PAR_SEANCE), une nouvelle personne n'est pas ajoutée : `e` revient tel quel. */
export function basculerPrevenir(e: ChoixEtat, cle: string, personneId: number): ChoixEtat {
  const c = lireChoix(e, cle);
  const deja = c.prevenir.includes(personneId);
  if (!deja && c.prevenir.length >= MAX_PREVENUS_PAR_SEANCE) return e;
  const n = cloner(e);
  n.choix[cle] = { ...c, prevenir: deja ? c.prevenir.filter((id) => id !== personneId) : [...c.prevenir, personneId] };
  return n;
}
export function mettreOff(e: ChoixEtat, cle: string, off: boolean): ChoixEtat {
  const n = cloner(e);
  if (off) { n.off[cle] = true; n.choix[cle] = { ...lireChoix(n, cle), remp: null, garant: null, avant: null }; }
  else delete n.off[cle];
  return n;
}

/** Instantané d'une séance avant « Retirer » (pour « Annuler »). */
export interface Instantane { cle: string; remp: number | null; garant: number | null; off: boolean; avant: ChoixSeance['avant'] }
export const instantane = (e: ChoixEtat, cle: string): Instantane => {
  const c = lireChoix(e, cle);
  return { cle, remp: c.remp, garant: c.garant, off: !!e.off[cle], avant: c.avant };
};
export function restaurer(e: ChoixEtat, snaps: Instantane[]): ChoixEtat {
  const n = cloner(e);
  for (const s of snaps) {
    n.choix[s.cle] = { ...lireChoix(n, s.cle), remp: s.remp, garant: s.garant, avant: s.avant };
    if (s.off) n.off[s.cle] = true; else delete n.off[s.cle];
  }
  return n;
}

// ── Autre activité ce jour-là (texte libre, jamais une demande) ─────────────────────────────────────────
/** « Autre activité — mar. 10 nov. : Bilan · mer. 11 nov. : RDV », seulement pour les jours choisis ; null s'il n'y en a pas. */
export function notesAutreActivite(notesJour: Record<DateIso, string>, jours: DateIso[]): string | null {
  const parts = [...jours].sort().filter((d) => (notesJour[d] ?? '').trim()).map((d) => `${jourCourt(d)} : ${notesJour[d].trim()}`);
  return parts.length ? `Autre activité — ${parts.join(' · ')}` : null;
}
export const aUneAutreActivite = (notesJour: Record<DateIso, string>, jours: DateIso[]): boolean => notesAutreActivite(notesJour, jours) != null;

// ── Corps de la requête de création ─────────────────────────────────────────────────────────────────────
export interface EntreesCorps {
  absentId: number;
  jours: DateIso[];
  seances: Seance[];
  e: ChoixEtat;
  personnes: Record<number, Personne>;
  notesJour: Record<DateIso, string>;
  notes: string;
  flex: boolean;
  motif: 'vacances' | 'autre';
  tipee: boolean;
  horsSoldeConfirme: boolean;
  /** Personnes joignables par « Prévenir aussi personnellement » ; le serveur contrôle encore. */
  peutPrevenir: (personneId: number) => boolean;
}
/**
 * Corps de POST /demandes. `dates` = TOUS les jours d'absence choisis : c'est ce que le calendrier montre et ce que les RH voient dans Tipee, et
 * le contrôle du solde de vacances compte ces jours-là. Les séances « Déjà demandé » et « Pas de remplaçant·e à trouver » ne sont pas envoyées.
 * Le message personnel ne part que pour une séance « À rechercher » (une séance confiée à quelqu'un n'en envoie pas).
 */
export function corpsCreation(x: EntreesCorps): CorpsCreation {
  const ateliers = x.seances.flatMap<LigneCreation>((s) => {
    const st = statut(s, x.e);
    const c = lireChoix(x.e, s.cle);
    if (st === 'trouve' && c.remp != null) {
      const stagiaire = !!x.personnes[c.remp]?.is_stagiaire;
      return [{
        atelier_id: s.atelierId, date_atelier: s.date, resolution: 'remplacant' as const,
        remplacant: { personne_id: c.remp, stagiaire, garant_personne_id: stagiaire && c.garant != null ? c.garant : undefined },
      }];
    }
    if (st === 'rech') {
      const prevenir = c.prevenir.filter(x.peutPrevenir);
      return [{ atelier_id: s.atelierId, date_atelier: s.date, resolution: 'recherche' as const, ...(prevenir.length ? { prevenir_ids: prevenir } : {}) }];
    }
    return [];
  });
  const autre = notesAutreActivite(x.notesJour, x.jours);
  const notes = [x.notes.trim(), autre ?? ''].filter(Boolean).join('\n\n');
  return {
    absent_personne_id: x.absentId,
    dates: [...x.jours].sort(),
    ateliers,
    dates_flexibles: x.flex,
    notes: notes || undefined,
    tipee_rappel_vu: x.tipee,
    motif_absence: x.motif,
    hors_solde_confirme: x.horsSoldeConfirme,
  };
}
